import React, { useEffect, useRef, useState } from 'react';
import { useSupportNotifications } from '../../../hooks/useSupportNotifications';
import { SupportNotification } from '../../../services/supportNotificationService';
import { WorkspaceThemeClasses } from './workspaceTheme';

interface SupportNotificationBadgeProps {
  theme: WorkspaceThemeClasses;
  inboxId?: string;
  onNavigate?: (conversationId: string) => void;
}

const priorityColors: Record<string, string> = {
  Urgent: 'bg-red-500',
  High: 'bg-orange-500',
  Normal: 'bg-blue-500',
  Low: 'bg-gray-400',
};

function timeAgo(dateStr?: string | null): string {
  if (!dateStr)
  {
    return '';
  }

  const parsed = new Date(dateStr).getTime();
  if (Number.isNaN(parsed))
  {
    return '';
  }

  const seconds = Math.floor((Date.now() - parsed) / 1000);

  if (seconds < 60)
  {
    return 'agora';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
  {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24)
  {
    return `${hours}h`;
  }

  return `${Math.floor(hours / 24)}d`;
}

export const SupportNotificationBadge: React.FC<
  SupportNotificationBadgeProps
> = ({ theme, inboxId, onNavigate }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useSupportNotifications(inboxId);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen)
    {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node))
      {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const handleClick = async (notification: SupportNotification) => {
    if (!notification.read)
    {
      await markAsRead(notification.id);
    }

    if (notification.conversationId && onNavigate)
    {
      onNavigate(notification.conversationId);
    }

    setIsOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors hover:bg-gray-100 ${theme.muted}`}
        title="Notificações"
        aria-label="Notificações"
        aria-expanded={isOpen}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[18px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 top-full mt-2 w-96 rounded-lg shadow-lg border z-50 max-h-[480px] flex flex-col ${theme.surface} ${theme.border}`}
        >
          <div className={`flex items-center justify-between px-4 py-3 border-b ${theme.border}`}>
            <h3 className={`text-sm font-semibold ${theme.text}`}>
              Notificações
            </h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className={`px-4 py-8 text-center text-sm ${theme.muted}`}>
                Nenhuma notificação
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => void handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b transition-colors hover:bg-gray-50 ${theme.border} ${
                    !n.read
                      ? 'bg-blue-50 border-l-4 border-l-blue-500'
                      : 'bg-white opacity-80'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ring-2 ring-white ${
                        !n.read
                          ? priorityColors[n.priority] || 'bg-blue-500'
                          : 'bg-gray-300'
                      }`}
                      title={!n.read ? 'Não lida' : 'Lida'}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`text-sm truncate ${
                            !n.read
                              ? `font-semibold ${theme.text}`
                              : `font-normal ${theme.muted}`
                          }`}
                        >
                          {n.title}
                        </span>
                        <span className={`text-xs flex-shrink-0 ${theme.faint}`}>
                          {timeAgo(n.createdAt)}
                        </span>
                      </div>
                      <p
                        className={`text-xs mt-0.5 line-clamp-2 ${
                          !n.read ? theme.text : theme.muted
                        }`}
                      >
                        {n.message}
                      </p>
                      {n.inboxName && (
                        <span className={`inline-block mt-1 text-xs px-1.5 py-0.5 rounded ${theme.badge}`}>
                          {n.inboxName}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportNotificationBadge;
